import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
 User, Mail, MapPin, Home, Lock, ArrowRight, KeyRound, Gift,
 ShieldCheck, BadgeDollarSign, Wallet, Check,
 Tag, Star, Store, Ban, Users, Search,
} from 'lucide-react';
import { NofeeNavLogo } from '../components/NoFeeLogo';
import AgreementCheckbox from '../components/AgreementCheckbox';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------- Local sub-components (kept inline for atomic page) ----------

const StripeWordmark = ({ className = 'h-5 w-auto' }) => (
 <svg viewBox="0 0 60 25" className={className} fill="none" aria-label="Stripe">
 <path d="M59.5 14.4c0-4.3-2.1-7.7-6.1-7.7s-6.4 3.4-6.4 7.7c0 5.1 2.9 7.6 7 7.6 2 0 3.5-.5 4.7-1.1v-3.4c-1.2.6-2.5 1-4.2 1-1.7 0-3.1-.6-3.3-2.5h8.3v-1.6zm-8.4-1.6c0-1.9 1.2-2.7 2.2-2.7 1.1 0 2.2.8 2.2 2.7h-4.4zM41.5 6.7c-1.7 0-2.7.8-3.3 1.3l-.2-1H34V26l4.2-.9V21c.6.4 1.5 1 3 1 3.1 0 5.9-2.5 5.9-7.7-.1-4.7-3-7.6-5.6-7.6zm-1 11.5c-1 0-1.6-.4-2-.8v-6.5c.5-.4 1.1-.8 2-.8 1.6 0 2.7 1.8 2.7 4 0 2.4-1.1 4.1-2.7 4.1zM33.2 5.4l4.2-.9V1L33.2 2v3.4zM33.2 7h4.2v14.7h-4.2V7zM28.7 8.2L28.4 7h-3.6v14.7h4.2v-10c1-1.3 2.7-1 3.2-.9V7c-.6-.2-2.5-.6-3.5 1.2zM20.2 3.4l-4.1.9v13.5c0 2.5 1.9 4.3 4.4 4.3 1.4 0 2.4-.3 3-.6V18c-.5.2-3.3 1-3.3-1.5V10.5h3.3V7h-3.3V3.4zM4.2 11.1c0-.7.5-.9 1.4-.9 1.2 0 2.8.4 4 1.1V7.4c-1.3-.5-2.6-.8-4-.8C2.4 6.6 0 8.4 0 11.4c0 4.7 6.3 3.9 6.3 5.9 0.8-.7 1-1.6 1-1.3 0-3-.6-4.3-1.3v4c1.5.6 2.9.9 4.3.9 3.4 0 5.9-1.6 5.9-4.7-.1-5-6.4-4.1-6.4-6.1z" fill="#635BFF"/>
 </svg>
);

// ---------- Top Navigation (customer page variant) ----------

const CustomerNav = () => {
 const links = [
 { label: 'How It Works', href: '/#how-it-works' },
 { label: 'Restaurants', href: '/restaurants' },
 { label: 'Rewards', href: '/rewards' },
 { label: 'About Us', href: '/about' },
 { label: 'Help', href: '/#faq' },
 ];
 return (
 <header className="absolute top-0 inset-x-0 z-30 bg-transparent">
 <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
 <a href="/" className="flex items-center" data-testid="customer-nav-logo">
 <NofeeNavLogo height={44} />
 </a>
 <nav className="hidden lg:flex items-center gap-7" data-testid="customer-nav-links">
 {links.map(l => (
 <a key={l.label} href={l.href}
 className="text-sm font-medium text-white/75 hover:text-gold-300 transition-colors">
 {l.label}
 </a>
 ))}
 </nav>
 <div className="flex items-center gap-2 sm:gap-3">
 <a href="/login?role=restaurant"
 data-testid="customer-nav-restaurant-login"
 className="hidden sm:inline-flex px-3 py-2 text-sm font-medium text-white/70 hover:text-gold-300 transition-colors">
 Restaurant Login
 </a>
 <a href="/customer?mode=login"
 data-testid="customer-nav-customer-login"
 className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-gold-500/95 hover:bg-gold-400 text-black text-sm font-bold transition-all shadow-[0_8px_25px_-10px_rgba(212,175,55,0.65)]">
 <User className="w-4 h-4" />
 Customer Login
 </a>
 <a href="/customer?mode=signup"
 data-testid="customer-nav-get-started"
 className="hidden md:inline-flex px-4 py-2 text-sm font-bold text-gold-300 border border-gold-500/40 hover:border-gold-500/70 hover:bg-gold-500/10 rounded-xl transition-all">
 Get Started Free
 </a>
 </div>
 </div>
 </header>
 );
};

// iter89 (Feb 2026) — Module-scope lookup tables for address ↔ ZIP cross-check
// (client mirror of the backend `detect_address_state_mismatch`). Kept at
// module scope so React Compiler can elide all the memoization plumbing.
const _STATE_NAMES = {
 alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR',
 california: 'CA', colorado: 'CO', connecticut: 'CT', delaware: 'DE',
 florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID',
 illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS',
 kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
 massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
 missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
 ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA',
 'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD',
 tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
 virginia: 'VA', washington: 'WA', 'west virginia': 'WV',
 wisconsin: 'WI', wyoming: 'WY',
};
const _STATE_ABBREVS = new Set(Object.values(_STATE_NAMES));
const _MAJOR_CITY_STATE = {
 'new york': 'NY', nyc: 'NY', brooklyn: 'NY', queens: 'NY', manhattan: 'NY', bronx: 'NY',
 'los angeles': 'CA', 'san francisco': 'CA', 'san diego': 'CA', oakland: 'CA', sacramento: 'CA',
 chicago: 'IL', houston: 'TX', dallas: 'TX', austin: 'TX', 'san antonio': 'TX',
 phoenix: 'AZ', tucson: 'AZ', philadelphia: 'PA', pittsburgh: 'PA',
 miami: 'FL', orlando: 'FL', tampa: 'FL', jacksonville: 'FL', 'fort lauderdale': 'FL', kissimmee: 'FL',
 boston: 'MA', cambridge: 'MA', worcester: 'MA',
 seattle: 'WA', denver: 'CO', atlanta: 'GA', detroit: 'MI', minneapolis: 'MN',
 'new orleans': 'LA', nashville: 'TN', memphis: 'TN', charlotte: 'NC', raleigh: 'NC',
 indianapolis: 'IN', columbus: 'OH', cleveland: 'OH', cincinnati: 'OH',
 louisville: 'KY', milwaukee: 'WI', 'kansas city': 'MO', 'saint louis': 'MO',
 portland: 'OR', newark: 'NJ', 'jersey city': 'NJ', 'las vegas': 'NV',
 'salt lake city': 'UT', 'oklahoma city': 'OK', tulsa: 'OK', honolulu: 'HI',
 providence: 'RI', hartford: 'CT', baltimore: 'MD', richmond: 'VA',
 'virginia beach': 'VA', albuquerque: 'NM', boise: 'ID', 'des moines': 'IA',
 omaha: 'NE', wichita: 'KS', anchorage: 'AK', 'little rock': 'AR',
 birmingham: 'AL', 'sioux falls': 'SD', fargo: 'ND', burlington: 'VT',
 manchester: 'NH', wilmington: 'DE', cheyenne: 'WY', billings: 'MT',
};

function detectAddressMismatch(address, expState, expCity) {
 if (!address ||!expState) return null;
 const text = ` ${address.toLowerCase().replace(/[^a-z\s,]/g, ' ')} `;
 for (const abbr of _STATE_ABBREVS) {
 if (abbr === expState) continue;
 const re = new RegExp(`(?:^|[\\s,])${abbr.toLowerCase()}(?=[\\s,]|$)`);
 if (re.test(text)) {
 return {
 message: `Your ZIP code resolves to ${expState}, but your address mentions ${abbr}. Please make sure the address and ZIP code are in the same state.`,
 };
 }
 }
 for (const [name, abbr] of Object.entries(_STATE_NAMES)) {
 if (abbr === expState) continue;
 const re = new RegExp(`(?:^|[\\s,])${name.replace(/ /g, '\\s')}(?:[\\s,]|$)`);
 if (re.test(text)) {
 return {
 message: `Your ZIP code resolves to ${expState}, but your address mentions ${name.replace(/\b\w/g, (c) => c.toUpperCase())}. Please make sure the address and ZIP code are in the same state.`,
 };
 }
 }
 for (const [city, abbr] of Object.entries(_MAJOR_CITY_STATE)) {
 if (abbr === expState) continue;
 if (expCity && city === expCity) continue;
 const re = new RegExp(`(?:^|[\\s,])${city.replace(/ /g, '\\s')}(?:[\\s,]|$)`);
 if (re.test(text)) {
 const cityTitle = city.replace(/\b\w/g, (c) => c.toUpperCase());
 return {
 message: `Your ZIP code resolves to ${expState}, but the address mentions ${cityTitle} which is in ${abbr}. Please correct the address or the ZIP code.`,
 };
 }
 }
 return null;
}

// ---------- Signup Form ----------

const SignupForm = () => {
 // Pre-fill referral code from?ref= query param (clicked from a referral link)
 const refFromUrl = (() => {
 try {
 const u = new URL(window.location.href);
 return (u.searchParams.get('ref') || '').toUpperCase();
 } catch { return ''; }
 })();
 const [form, setForm] = useState({
 name: '', email: '', password: '', zip_code: '', address: '',
 referral_code: refFromUrl,
 });
 const [submitting, setSubmitting] = useState(false);
 const [success, setSuccess] = useState(false);
 // iter125 — Customer Agreement consent. Mandatory before account creation.
 const [acceptedAgreement, setAcceptedAgreement] = useState(false);
 const [agreementError, setAgreementError] = useState('');
 // iter125 — Customers are auto-enrolled into the Rewards Program at
 // signup, so the Rewards Program Terms acceptance is captured here at
 // the moment of enrollment (per the new legal flow). Separate checkbox
 // so the user sees and accepts each agreement on its own merits.
 const [acceptedRewards, setAcceptedRewards] = useState(false);
 const [rewardsError, setRewardsError] = useState('');
 // ZIP-code → city/state resolution (iter88, Jun 2026). The customer
 // signup previously accepted any 5-digit string, which let users sign
 // up with a Boston street address + Florida ZIP. We now resolve the
 // ZIP server-side via the same Zippopotam.us-backed endpoint the
 // restaurant onboarding uses and SHOW the resolved city + state so
 // the customer can spot a mismatch immediately. Invalid ZIPs block
 // submission.
 const [zipLookup, setZipLookup] = useState({ status: 'idle', city: '', state: '', error: '' });

 // iter89 (Feb 2026) — Address ↔ ZIP cross-check (client mirror of the
 // backend `detect_address_state_mismatch`). We block the submit (and
 // show an inline warning) when the typed address mentions a state
 // abbrev / state name / major city that contradicts the ZIP's
 // resolved state. The backend re-runs the same check authoritatively.
 const addressMismatch =
 zipLookup.status === 'ok'
? detectAddressMismatch(form.address, (zipLookup.state || '').toUpperCase(), (zipLookup.city || '').toLowerCase())
: null;

 const onChange = (k) => (e) => setForm(s => ({...s, [k]: e.target.value }));

 // Debounced ZIP lookup — fires once the field has exactly 5 digits.
 useEffect(() => {
 const zip = form.zip_code.trim();
 let cancelled = false;
 const t = setTimeout(() => {
 const run = async () => {
 if (!/^\d{5}$/.test(zip)) {
 setZipLookup({ status: 'idle', city: '', state: '', error: '' });
 return;
 }
 setZipLookup({ status: 'loading', city: '', state: '', error: '' });
 try {
 const r = await axios.get(`${API}/api/public/zip-lookup/${encodeURIComponent(zip)}`);
 if (cancelled) return;
 if (r.data?.valid) {
 setZipLookup({
 status: 'ok',
 city: r.data.city || '',
 state: r.data.state || '',
 error: '',
 });
 } else {
 setZipLookup({ status: 'invalid', city: '', state: '', error: 'Not a recognized US ZIP code.' });
 }
 } catch {
 if (!cancelled) setZipLookup({ status: 'invalid', city: '', state: '', error: 'Could not verify this ZIP code.' });
 }
 };
 run();
 }, 0);
 return () => { cancelled = true; clearTimeout(t); };
 }, [form.zip_code]);

 const submit = async (e) => {
 e.preventDefault();
 setAgreementError('');
 if (!form.name.trim() ||!form.email.trim() ||!form.password.trim() ||!form.zip_code.trim()) {
 toast.error('Please fill in your name, email, password and ZIP code.');
 return;
 }
 if (form.password.length < 6) {
 toast.error('Password must be at least 6 characters.');
 return;
 }
 // iter125 — Customer Agreement must be ticked before account creation.
 if (!acceptedAgreement) {
 setAgreementError('Please review and accept the Customer Agreement to continue.');
 toast.error('Please review and accept the Customer Agreement to continue.');
 return;
 }
 // iter125 — Rewards Program Terms acceptance captured at enrollment.
 if (!acceptedRewards) {
 setRewardsError('Please review and accept the Rewards Program Terms to continue.');
 toast.error('Please review and accept the Rewards Program Terms to continue.');
 return;
 }
 // Block submission until the ZIP code has resolved to a real
 // US city/state. Prevents the "Boston address + Florida ZIP"
 // mismatch the owner reported in iter88.
 if (zipLookup.status === 'loading') {
 toast.error('Verifying your ZIP code…');
 return;
 }
 if (zipLookup.status!== 'ok') {
 toast.error('Please enter a valid US ZIP code.');
 return;
 }
 // Address ↔ ZIP cross-check — same logic the backend runs, but
 // surfaced here for instant feedback so the user can correct
 // before submitting.
 if (addressMismatch) {
 toast.error(addressMismatch.message);
 return;
 }
 // iter91 — real-address geocoding via the shared validator endpoint.
 // Catches typos / non-existent street names before submit.
 if (form.address && form.address.trim().length >= 3) {
 try {
 const v = await axios.post(`${API}/api/public/validate-address`, {
 street: form.address.trim(),
 city: zipLookup.city,
 state: zipLookup.state,
 zip_code: form.zip_code.trim(),
 });
 if (!v.data?.valid) {
 toast.error(v.data?.error_message || 'Please correct your address.');
 return;
 }
 } catch {
 // Soft-fail on a network blip — backend re-checks anyway.
 }
 }
 setSubmitting(true);
 try {
 // Send the resolved city/state alongside the form so the backend
 // can persist them on the customer doc without re-running the
 // lookup. Backwards-compatible — older clients that don't ship
 // these fields still work.
 const payload = {
...form,
 city: zipLookup.city,
 state: zipLookup.state,
 };
 const res = await axios.post(`${API}/api/customers/auth/register`, payload);
 if (res.data?.token) {
 localStorage.setItem('nofee_customer_token', res.data.token);
 setSuccess(true);
 toast.success('Account created! Welcome to NoFee.');
 setTimeout(() => { window.location.href = '/customer/rewards'; }, 700);
 } else {
 toast.error('Could not create your account.');
 }
 } catch (err) {
 toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
 } finally {
 setSubmitting(false);
 }
 };

 const inputBase =
 'w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/10 ' +
 'text-white placeholder-white/45 text-[15px] ' +
 'focus:outline-none focus:border-gold-500/60 focus:bg-white/[0.06] ' +
 'transition-colors';

 return (
 <form
 id="signup-form"
 onSubmit={submit}
 data-testid="customer-signup-form"
 className="space-y-3"
 >
 <div className="space-y-3">
 <div className="relative">
 <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gold-400/80" />
 <input type="text" placeholder="Full Name"
 value={form.name} onChange={onChange('name')}
 data-testid="signup-input-name"
 className={inputBase} required />
 </div>
 <div className="relative">
 <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gold-400/80" />
 <input type="email" placeholder="Email Address"
 value={form.email} onChange={onChange('email')}
 data-testid="signup-input-email"
 className={inputBase} required />
 </div>
 <div className="relative">
 <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gold-400/80" />
 <input type="password" placeholder="Password (min. 6 chars)"
 value={form.password} onChange={onChange('password')}
 data-testid="signup-input-password"
 className={inputBase} required minLength={6} />
 </div>
 <div className="relative">
 <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gold-400/80" />
 <input type="text" placeholder="ZIP Code"
 value={form.zip_code} onChange={onChange('zip_code')}
 data-testid="signup-input-zip"
 inputMode="numeric"
 maxLength={5}
 className={inputBase} required />
 </div>
 {/* iter88 (Jun 2026) — show the resolved city/state so users
 can spot mismatches (e.g. they entered a Boston street
 address but a Miami ZIP code). Three states: loading, ok,
 invalid. Empty / non-5-digit input renders nothing. */}
 {zipLookup.status === 'loading' && (
 <p
 data-testid="signup-zip-status"
 data-status="loading"
 className="-mt-1 ml-1 text-[12px] text-white/55 flex items-center gap-1.5"
 >
 <span className="inline-block w-3 h-3 border-2 border-gold-400/70 border-t-transparent rounded-full animate-spin" />
 Verifying ZIP…
 </p>
 )}
 {zipLookup.status === 'ok' && (
 <p
 data-testid="signup-zip-status"
 data-status="ok"
 className="-mt-1 ml-1 text-[12px] text-emerald-300 flex items-center gap-1.5"
 >
 <Check className="w-3.5 h-3.5" />
 <span>
 ZIP {form.zip_code} → <strong className="font-semibold">{zipLookup.city}, {zipLookup.state}</strong>
 </span>
 </p>
 )}
 {zipLookup.status === 'invalid' && (
 <p
 data-testid="signup-zip-status"
 data-status="invalid"
 className="-mt-1 ml-1 text-[12px] text-rose-300"
 >
 {zipLookup.error || 'Please enter a valid US ZIP code.'}
 </p>
 )}
 <div className="relative">
 <Home className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gold-400/80" />
 <input type="text" placeholder="Street Address (e.g. 123 Main St)"
 value={form.address} onChange={onChange('address')}
 data-testid="signup-input-address"
 className={inputBase} />
 </div>
 {addressMismatch && (
 <p
 data-testid="signup-address-mismatch"
 className="-mt-1 ml-1 text-[12px] text-rose-300 leading-snug"
 >
 {addressMismatch.message}
 </p>
 )}
 <div className="relative">
 <Gift className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gold-400/60" />
 <input type="text" placeholder="Referral Code (Optional)"
 value={form.referral_code} onChange={(e) => setForm(s => ({...s, referral_code: e.target.value.toUpperCase()}))}
 data-testid="signup-input-referral"
 className={inputBase} />
 </div>
 </div>

 <button type="submit" disabled={submitting || success}
 data-testid="signup-submit-btn"
 className="mt-2 w-full py-3.5 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 text-black font-extrabold text-[15px] tracking-tight
 hover:from-amber-200 hover:to-gold-400 hover:-translate-y-0.5 active:translate-y-0
 disabled:opacity-60 disabled:cursor-not-allowed
 transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]">
 {success? '✓ Account Created': submitting? 'Creating…': 'Create Free Account'}
 </button>

 {/* iter125 — Customer Agreement consent, mandatory before submit. */}
 <div className="pt-1">
 <AgreementCheckbox
 checked={acceptedAgreement}
 onChange={(v) => {
 setAcceptedAgreement(v);
 if (v) setAgreementError('');
 }}
 agreementLabel="Customer Agreement"
 agreementHref="/customer-agreement"
 testid="customer-agreement-checkbox"
 errorText={agreementError}
 />
 </div>
 {/* iter125 — Rewards Program Terms acceptance, captured at enrollment. */}
 <div className="pt-1">
 <AgreementCheckbox
 checked={acceptedRewards}
 onChange={(v) => {
 setAcceptedRewards(v);
 if (v) setRewardsError('');
 }}
 agreementLabel="Rewards Program Terms"
 agreementHref="/rewards-terms"
 testid="customer-rewards-terms-checkbox"
 errorText={rewardsError}
 />
 </div>
 </form>
 );
};

// ---------- Tabbed Customer Auth (LOGIN default + Signup secondary) ----------

const SignInForm = ({ onForgot }) => {
 // 'password' | 'magic-request' | 'magic-sent'
 const [view, setView] = useState('password');
 const [form, setForm] = useState({ email: '', password: '' });
 const [submitting, setSubmitting] = useState(false);

 // Listen for the page-level "Forgot password?" button to route into the
 // magic-link flow (the most secure passwordless recovery path).
 React.useEffect(() => {
 const onOpen = () => setView('magic-request');
 window.addEventListener('nofee:open-magic-link', onOpen);
 return () => window.removeEventListener('nofee:open-magic-link', onOpen);
 }, []);

 const onChange = (k) => (e) => setForm((s) => ({...s, [k]: e.target.value }));

 const submit = async (e) => {
 e.preventDefault();
 if (!form.email.trim() ||!form.password.trim()) {
 toast.error('Enter your email and password to sign in.');
 return;
 }
 setSubmitting(true);
 try {
 const res = await axios.post(`${API}/api/customers/auth/login`, {
 email: form.email.trim().toLowerCase(),
 password: form.password,
 });
 if (res.data?.token) {
 localStorage.setItem('nofee_customer_token', res.data.token);
 toast.success('Welcome back!');
 setTimeout(() => {
 window.location.href = '/customer/rewards';
 }, 400);
 } else {
 toast.error('Could not sign you in. Please check your credentials.');
 }
 } catch (err) {
 toast.error(err?.response?.data?.detail || 'Invalid email or password.');
 } finally {
 setSubmitting(false);
 }
 };

 const submitMagicLink = async (e) => {
 e.preventDefault();
 const email = form.email.trim().toLowerCase();
 if (!email ||!email.includes('@')) {
 toast.error('Enter a valid email to receive your sign-in link.');
 return;
 }
 setSubmitting(true);
 try {
 await axios.post(`${API}/api/customers/auth/magic-link`, { email });
 // Response is intentionally uniform whether or not the account exists.
 setView('magic-sent');
 } catch (err) {
 toast.error(err?.response?.data?.detail || 'Could not send your sign-in link. Try again.');
 } finally {
 setSubmitting(false);
 }
 };

 const inputBase =
 'w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/10 ' +
 'text-white placeholder-white/45 text-[15px] ' +
 'focus:outline-none focus:border-gold-500/60 focus:bg-white/[0.06] transition-colors';

 // ─── Magic link: confirmation view ────────────────────────────────────
 if (view === 'magic-sent') {
 return (
 <div data-testid="magic-link-sent" className="text-center py-3">
 <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/30 flex items-center justify-center mb-4">
 <Mail className="w-6 h-6 text-emerald-300" strokeWidth={1.75} />
 </div>
 <h3 className="text-white text-[17px] font-bold tracking-tight">Check your inbox</h3>
 <p className="mt-2 text-[13px] text-white/60 leading-relaxed">
 If an account exists for <strong className="text-white">{form.email.trim().toLowerCase()}</strong>, a one-click sign-in link is on its way. The link expires in 15 minutes.
 </p>
 <button
 type="button"
 onClick={() => setView('password')}
 data-testid="magic-link-back-to-password"
 className="mt-5 text-[13px] text-gold-300 hover:text-gold-200 font-bold"
 >
 Back to password sign in
 </button>
 </div>
 );
 }

 // ─── Magic link: request view ─────────────────────────────────────────
 if (view === 'magic-request') {
 return (
 <form onSubmit={submitMagicLink} data-testid="magic-link-form" className="space-y-3">
 <div className="relative">
 <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gold-400/80" />
 <input
 type="email"
 placeholder="Email Address"
 value={form.email}
 onChange={onChange('email')}
 data-testid="magic-link-input-email"
 className={inputBase}
 required
 autoFocus
 />
 </div>
 <button
 type="submit"
 disabled={submitting}
 data-testid="magic-link-submit-btn"
 className="mt-2 w-full py-3.5 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 text-black font-extrabold text-[15px] tracking-tight hover:from-amber-200 hover:to-gold-400 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]"
 >
 {submitting? 'Sending…': 'Email me a sign-in link'}
 </button>
 <p className="text-center text-[12.5px] text-white/55">
 Prefer your password?{' '}
 <button
 type="button"
 onClick={() => setView('password')}
 data-testid="magic-link-switch-to-password"
 className="text-gold-300 hover:text-gold-200 font-bold"
 >
 Sign in with password
 </button>
 </p>
 </form>
 );
 }

 // ─── Default: password sign-in ────────────────────────────────────────
 return (
 <form onSubmit={submit} data-testid="customer-login-form" className="space-y-3">
 <div className="relative">
 <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gold-400/80" />
 <input
 type="email"
 placeholder="Email Address"
 value={form.email}
 onChange={onChange('email')}
 data-testid="login-input-email"
 className={inputBase}
 required
 autoFocus
 />
 </div>
 <div className="relative">
 <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gold-400/80" />
 <input
 type="password"
 placeholder="Password"
 value={form.password}
 onChange={onChange('password')}
 data-testid="login-input-password"
 className={inputBase}
 required
 />
 </div>

 <div className="flex items-center justify-end">
 <button
 type="button"
 onClick={onForgot}
 data-testid="login-forgot-password"
 className="text-[12px] text-gold-300/85 hover:text-gold-200 font-medium transition-colors"
 >
 Forgot password?
 </button>
 </div>

 <button
 type="submit"
 disabled={submitting}
 data-testid="login-submit-btn"
 className="mt-2 w-full py-3.5 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 text-black font-extrabold text-[15px] tracking-tight hover:from-amber-200 hover:to-gold-400 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]"
 >
 {submitting? 'Signing in…': 'Sign in'}
 </button>

 {/* Magic-link entrypoint — passwordless alternative */}
 <div className="relative my-2 flex items-center gap-3" aria-hidden="true">
 <span className="flex-1 h-px bg-white/10" />
 <span className="text-[10px] text-white/40 uppercase tracking-[0.2em] font-bold">or</span>
 <span className="flex-1 h-px bg-white/10" />
 </div>
 <button
 type="button"
 onClick={() => setView('magic-request')}
 data-testid="open-magic-link"
 className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border border-white/12 hover:border-gold-500/40 bg-white/[0.02] hover:bg-white/[0.04] text-white/85 hover:text-white font-bold text-[13.5px] transition-all"
 >
 <Mail className="w-4 h-4 text-gold-400/85" />
 Email me a sign-in link
 </button>
 </form>
 );
};

const CustomerAuthForm = () => {
 // Read initial mode from URL: /customer?mode=login OR /customer?mode=signup.
 // Defaults to LOGIN (per UX rule — returning users dominate the click).
 const initialMode = (() => {
 try {
 const u = new URL(window.location.href);
 const m = (u.searchParams.get('mode') || '').toLowerCase();
 if (m === 'signup' || m === 'register' || u.hash === '#signup') return 'signup';
 } catch {
 /* ignore */
 }
 return 'login';
 })();

 const [mode, setMode] = useState(initialMode);

 // Forgot password → magic link is the simplest secure recovery flow. The
 // user clicks "Forgot password?", an empty magic-link request form is shown
 // (the SignInForm's internal 'magic-request' view). We use a window event
 // because the SignInForm owns that local state.
 const handleForgotPassword = () => {
 window.dispatchEvent(new CustomEvent('nofee:open-magic-link'));
 };

 return (
 <div
 id="signup"
 data-testid="customer-auth-card"
 className="mt-9 max-w-[480px] rounded-2xl bg-black/55 backdrop-blur-md border border-gold-500/25 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] p-6 sm:p-7"
 >
 {/* Tab switcher — Sign in is the DEFAULT active tab. */}
 <div
 className="grid grid-cols-2 p-1 mb-5 rounded-2xl bg-white/[0.04] border border-white/8"
 data-testid="customer-auth-tabs"
 role="tablist"
 >
 <button
 type="button"
 role="tab"
 aria-selected={mode === 'login'}
 onClick={() => setMode('login')}
 data-testid="customer-auth-tab-login"
 className={`py-2.5 rounded-xl text-[13px] font-bold transition-all ${
 mode === 'login'
? 'bg-gradient-to-b from-amber-300 to-gold-500 text-black shadow-[0_8px_20px_-8px_rgba(212,175,55,0.55)]'
: 'text-white/65 hover:text-white'
 }`}
 >
 Sign in
 </button>
 <button
 type="button"
 role="tab"
 aria-selected={mode === 'signup'}
 onClick={() => setMode('signup')}
 data-testid="customer-auth-tab-signup"
 className={`py-2.5 rounded-xl text-[13px] font-bold transition-all ${
 mode === 'signup'
? 'bg-gradient-to-b from-amber-300 to-gold-500 text-black shadow-[0_8px_20px_-8px_rgba(212,175,55,0.55)]'
: 'text-white/65 hover:text-white'
 }`}
 >
 Create account
 </button>
 </div>

 <h2 className="text-[20px] sm:text-[22px] font-extrabold text-white tracking-tight">
 {mode === 'login'? 'Welcome back': 'Create Your Free Account'}
 </h2>
 <p className="mt-1.5 text-[12px] text-gold-300/80 leading-snug">
 {mode === 'login'
? 'Sign in to access your NoFee account and rewards.'
: 'Earn rewards from day one. Use a referral code for bonus points.'}
 </p>

 <div className="mt-5">
 {mode === 'login'? <SignInForm onForgot={handleForgotPassword} />: <SignupForm />}
 </div>

 {/* Cross-link to the other mode (psychology rule: returning users see "Sign in" headline; new users get the path to Create account) */}
 <p className="mt-4 text-center text-[12.5px] text-white/55">
 {mode === 'login'? (
 <>
 Don&apos;t have an account?{' '}
 <button
 type="button"
 onClick={() => setMode('signup')}
 data-testid="customer-auth-switch-to-signup"
 className="text-gold-300 hover:text-gold-200 font-bold"
 >
 Create account
 </button>
 </>
 ): (
 <>
 Already a member?{' '}
 <button
 type="button"
 onClick={() => setMode('login')}
 data-testid="customer-auth-switch-to-login"
 className="text-gold-300 hover:text-gold-200 font-bold"
 >
 Sign in
 </button>
 </>
 )}
 </p>

 <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/55">
 <Lock className="w-3.5 h-3.5 text-gold-400/80" />
 <span>Your information is safe and secure.</span>
 </div>
 </div>
 );
};

// ---------- Trust strip (3 horizontal benefits under form) ----------

const TrustStrip = () => {
 const items = [
 { icon: ShieldCheck, title: 'Secure Payments', sub: 'Powered by Stripe' },
 { icon: BadgeDollarSign, title: 'No Hidden Fees', sub: 'What you see is what you pay.' },
 { icon: Wallet, title: 'Restaurants Paid Directly', sub: 'NoFee never holds your money.' },
 ];
 return (
 <div className="mt-6 max-w-[480px] grid grid-cols-3 gap-3" data-testid="customer-trust-strip">
 {items.map(({ icon: Icon, title, sub }) => (
 <div key={title} className="flex items-start gap-2">
 <Icon className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" />
 <div className="leading-tight">
 <div className="text-[11px] font-bold text-white">{title}</div>
 <div className="text-[10px] text-white/50 mt-0.5">{sub}</div>
 </div>
 </div>
 ))}
 </div>
 );
};

// ---------- Benefits Grid (5 cards) ----------

const BenefitsGrid = () => {
 const cards = [
 { icon: Tag, title: 'Pay Less', sub: 'Enjoy lower prices because restaurants keep more.' },
 { icon: Star, title: 'Earn Rewards', sub: 'Earn points on every order and unlock exclusive rewards.' },
 { icon: Store, title: 'Request Restaurants', sub: 'Don’t see your favorite restaurant? Request it and earn rewards.' },
 { icon: Ban, title: 'No Hidden Fees', sub: 'No service fees. No surprises. Just better prices.' },
 { icon: Users, title: 'Better For Everyone', sub: 'We support local restaurants and build stronger communities.' },
 ];
 return (
 <section className="relative py-16 lg:py-20" data-testid="customer-benefits-grid">
 <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
 <div className="rounded-3xl border border-gold-500/15 bg-gradient-to-b from-zinc-950 to-black overflow-hidden">
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 divide-x divide-gold-500/10">
 {cards.map(({ icon: Icon, title, sub }) => (
 <div key={title} className="p-7 lg:p-8 text-center">
 <div className="mx-auto w-14 h-14 rounded-2xl border border-gold-500/30 bg-gold-500/[0.05] flex items-center justify-center mb-5">
 <Icon className="w-6 h-6 text-gold-400" strokeWidth={1.5} />
 </div>
 <h3 className="text-[15px] font-extrabold text-white mb-2 tracking-tight">{title}</h3>
 <p className="text-[12.5px] text-white/55 leading-relaxed">{sub}</p>
 </div>
 ))}
 </div>
 </div>
 </div>
 </section>
 );
};

// ---------- ZIP Code Search Section ----------

const ZipSearchSection = () => {
 const [zip, setZip] = useState('');
 const onSearch = (e) => {
 e.preventDefault();
 if (!/^\d{5}$/.test(zip.trim())) {
 toast.error('Please enter a valid 5-digit US ZIP code.');
 return;
 }
 window.location.href = `/restaurants?zip=${zip.trim()}`;
 };
 return (
 <section className="pb-10" data-testid="customer-zip-search">
 <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
 <div className="rounded-3xl border border-gold-500/15 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-8 lg:p-10">
 <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 items-center">
 <div>
 <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Find Restaurants Near You</h2>
 <p className="mt-2 text-sm text-white/55 leading-relaxed max-w-md">
 Enter your ZIP code to discover local restaurants with lower prices and no massive fees.
 </p>
 </div>
 <form onSubmit={onSearch} className="flex flex-col sm:flex-row gap-3">
 <div className="relative flex-1">
 <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gold-400/80" />
 <input
 inputMode="numeric"
 maxLength={5}
 placeholder="Enter your ZIP code"
 value={zip}
 onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, ''))}
 data-testid="zip-search-input"
 className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/[0.04] border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-gold-500/60 transition-colors text-[15px]"
 />
 </div>
 <button type="submit"
 data-testid="zip-search-submit"
 className="px-7 py-4 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold whitespace-nowrap transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]">
 Search Restaurants
 </button>
 </form>
 </div>
 <div className="mt-4 flex items-center gap-2 text-xs text-gold-400/80">
 <MapPin className="w-3.5 h-3.5" />
 <span>We’ll show you the best options in your area.</span>
 </div>
 </div>
 </div>
 </section>
 );
};

// ---------- Request Restaurant Section ----------

const RequestRestaurantSection = () => {
 const [open, setOpen] = useState(false);
 const [name, setName] = useState('');
 const [email, setEmail] = useState('');
 const [sending, setSending] = useState(false);

 const submit = async (e) => {
 e.preventDefault();
 if (!name.trim()) { toast.error('Please enter the restaurant name.'); return; }
 setSending(true);
 try {
 await axios.post(`${API}/api/customers/request-restaurant`, {
 restaurant_name: name, customer_email: email,
 });
 toast.success('Request submitted. We’ll reach out to them!');
 setOpen(false); setName(''); setEmail('');
 } catch (err) {
 toast.error('Could not submit your request.');
 } finally { setSending(false); }
 };

 return (
 <section className="py-10" data-testid="customer-request-restaurant">
 <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
 <div className="rounded-3xl border border-gold-500/15 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-8 lg:p-12 overflow-hidden">
 <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 items-center">
 {/* Left — text + CTA */}
 <div>
 <h2 className="text-[26px] sm:text-3xl lg:text-4xl font-black text-gold-400 leading-tight tracking-tight">
 Can’t find your favorite<br/>restaurant?
 </h2>
 <p className="mt-4 text-sm text-white/65 max-w-md leading-relaxed">
 Request a restaurant and we’ll bring it to NoFee. Earn rewards when they join.
 </p>
 <ul className="mt-6 space-y-3">
 {['You request it.','We reach out.','You get rewards when it joins!'].map(t => (
 <li key={t} className="flex items-center gap-3 text-[15px] text-white/85">
 <span className="w-6 h-6 rounded-full border border-gold-500/50 bg-gold-500/10 flex items-center justify-center">
 <svg className="w-3 h-3 text-gold-400" viewBox="0 0 20 20" fill="currentColor">
 <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4L8.5 12 15.3 5.3a1 1 0 011.4 0z" clipRule="evenodd"/>
 </svg>
 </span>
 {t}
 </li>
 ))}
 </ul>
 <button
 onClick={() => setOpen(true)}
 data-testid="request-restaurant-open-btn"
 className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]">
 Request a Restaurant <ArrowRight className="w-4 h-4" />
 </button>
 </div>

 {/* Right — premium rewards illustration */}
 <div className="relative h-[320px] lg:h-[380px] hidden md:block">
 {/* Soft gold glow */}
 <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_50%,rgba(212,175,55,0.18),transparent_60%)]" />

 {/* Phone */}
 <div className="absolute left-2 lg:left-10 top-1/2 -translate-y-1/2 w-[170px] lg:w-[200px] aspect-[9/19] rounded-[36px] bg-black border-2 border-zinc-800 shadow-2xl overflow-hidden">
 <div className="h-6 bg-black flex items-center justify-center"><div className="w-16 h-1.5 rounded-full bg-zinc-800"/></div>
 <div className="px-4 py-3">
 <div className="flex items-center gap-2">
 <span className="w-6 h-6 rounded-full border border-gold-500/60 flex items-center justify-center">
 <span className="w-2 h-2 rounded-full bg-gold-400" />
 </span>
 <span className="text-[11px] font-bold text-gold-400">NoFee</span>
 </div>
 <div className="mt-5 text-[11px] font-bold text-white">Request a Restaurant</div>
 <div className="mt-3 h-7 rounded-md bg-white/[0.05] border border-white/10 px-2 flex items-center text-[9px] text-white/40">Restaurant Name</div>
 <div className="mt-2 h-7 rounded-md bg-gold-500/90 flex items-center justify-center">
 <span className="text-[9px] font-extrabold text-black">Submit Request</span>
 </div>
 </div>
 </div>

 {/* Gift box */}
 <div className="absolute right-4 lg:right-8 top-6 lg:top-4">
 <div className="relative w-[140px] lg:w-[160px] h-[120px] lg:h-[140px]">
 <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-gold-500/30 shadow-2xl" />
 <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-3 bg-gradient-to-r from-amber-300 via-gold-400 to-amber-500" />
 <div className="absolute left-1/2 -translate-x-1/2 -top-3 w-12 h-12">
 <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-300 to-gold-500" style={{clipPath:'polygon(50% 0,100% 50%,75% 80%,50% 60%,25% 80%,0 50%)'}} />
 </div>
 </div>
 </div>

 {/* Rewards card */}
 <div className="absolute right-2 lg:right-4 bottom-2 lg:bottom-4 rotate-[8deg]">
 <div className="px-5 py-4 rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-gold-500/40 shadow-[0_25px_50px_-15px_rgba(0,0,0,0.8)]">
 <div className="text-[11px] font-bold text-white/70 flex items-center gap-1.5">
 <Star className="w-3 h-3 text-gold-400 fill-gold-400" /> You Earn
 </div>
 <div className="text-2xl font-black text-gold-400 leading-tight tracking-tight">Rewards!</div>
 </div>
 </div>

 {/* Coins */}
 <div className="absolute right-16 lg:right-24 bottom-16 flex gap-1.5">
 {[0,1,2].map(i => (
 <div key={i} className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-300 to-gold-500 border border-amber-200/40 shadow-md" />
 ))}
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* Modal */}
 {open && (
 <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="request-restaurant-modal">
 <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
 <form onSubmit={submit} className="relative w-full max-w-md rounded-2xl bg-zinc-950 border border-gold-500/30 p-6">
 <h3 className="text-xl font-extrabold text-white">Request a Restaurant</h3>
 <p className="mt-1 text-sm text-white/55">Tell us which restaurant you’d like to see on NoFee.</p>
 <input type="text" placeholder="Restaurant name" value={name} onChange={(e) => setName(e.target.value)}
 data-testid="request-restaurant-name-input"
 className="mt-4 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-gold-500/60" required />
 <input type="email" placeholder="Your email (optional)" value={email} onChange={(e) => setEmail(e.target.value)}
 data-testid="request-restaurant-email-input"
 className="mt-3 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-gold-500/60" />
 <div className="mt-5 flex gap-2">
 <button type="button" onClick={() => setOpen(false)} className="flex-1 py-3 rounded-xl border border-white/10 text-white/70 hover:text-white hover:border-white/30 transition-colors">Cancel</button>
 <button type="submit" disabled={sending} data-testid="request-restaurant-submit-btn"
 className="flex-1 py-3 rounded-xl bg-gold-500 hover:bg-gold-400 text-black font-bold disabled:opacity-60">
 {sending? 'Sending…': 'Submit'}
 </button>
 </div>
 </form>
 </div>
 )}
 </section>
 );
};

// ---------- Bottom Security Bar ----------

const BottomSecurityBar = () => (
 <section className="py-10 border-t border-white/5" data-testid="customer-bottom-security">
 <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
 <div className="flex items-center gap-5">
 <span className="text-[11px] uppercase tracking-widest text-white/45 font-semibold">Secure Payments With</span>
 <StripeWordmark />
 </div>
 <div className="flex items-center gap-2 text-xs text-white/50">
 <Lock className="w-3.5 h-3.5 text-gold-400" />
 <span>Your data is protected with industry-leading security.</span>
 </div>
 </div>
 </section>
);

// ---------- Page ----------

export default function CustomerOnboarding() {
 // Read intent once on mount — used to keep the hero eyebrow/sub-headline
 // consistent with the auth card's default tab (Sign in vs Create account).
 const heroMode = (() => {
 try {
 const u = new URL(window.location.href);
 const m = (u.searchParams.get('mode') || '').toLowerCase();
 if (m === 'signup' || m === 'register' || u.hash === '#signup') return 'signup';
 } catch {
 /* ignore */
 }
 return 'login';
 })();

 return (
 <div className="min-h-screen bg-black text-white antialiased" data-testid="customer-onboarding-page">
 <CustomerNav />

 {/* HERO */}
 <section className="relative pt-28 lg:pt-32 pb-12 lg:pb-20 overflow-hidden" data-testid="customer-hero">
 {/* Ambient gold glows */}
 <div className="pointer-events-none absolute inset-0 z-0">
 <div className="absolute top-[10%] right-[10%] w-[36rem] h-[36rem] bg-amber-500/[0.10] rounded-full blur-[120px]" />
 <div className="absolute bottom-0 left-1/3 w-[28rem] h-[28rem] bg-amber-600/[0.06] rounded-full blur-3xl" />
 </div>

 <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
 {/* LEFT */}
 <div className="relative z-10">
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/30 bg-gold-500/[0.06] backdrop-blur-sm mb-5" data-testid="customer-eyebrow">
 <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
 <span className="text-[10.5px] uppercase tracking-[0.22em] text-gold-300 font-bold">
 {heroMode === 'login'? 'Welcome back to NoFee': 'Create your free account'}
 </span>
 </div>
 <h1 className="text-[42px] sm:text-5xl lg:text-6xl xl:text-[64px] font-black tracking-tight leading-[1.05] text-white">
 Better Food.<br />
 Lower Prices.<br />
 <span className="text-gold-400">No Massive Fees.</span>
 </h1>
 <p className="mt-5 text-base sm:text-lg text-white/65 leading-relaxed max-w-[460px]">
 {heroMode === 'login'
? 'Sign in to access your NoFee account, rewards, and saved restaurants.'
: 'Sign up in 30 seconds and order from the best local restaurants — with lower prices and 0% commission going to delivery apps.'}
 </p>

 <CustomerAuthForm />
 <TrustStrip />
 </div>

 {/* RIGHT — Hero image */}
 <div className="relative" data-testid="customer-hero-image-wrapper">
 <div className="relative rounded-3xl overflow-hidden border border-gold-500/15 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] bg-zinc-950">
 <img
 src="/brand/customer_delivery.png"
 alt="Happy couple receiving NoFee delivery at home"
 className="w-full h-auto block select-none"
 draggable={false}
 data-testid="customer-hero-image"
 fetchPriority="high"
 />
 {/* Subtle vignette */}
 <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
 </div>
 </div>
 </div>
 </section>

 <BenefitsGrid />
 <ZipSearchSection />
 <RequestRestaurantSection />
 <BottomSecurityBar />
 </div>
 );
}
